import React, { FC, Fragment, useEffect } from 'react';
import { Accordion, Box, NumberPicker, Typography } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import { useDispatch } from 'react-redux';

import useSelector from '../../../../../hooks/useSelector';
import { getEtasje } from '../../../../../store/registration/selectors';
import { addGulvsone, removeGulvsone, updateGulvsone } from '../../../../../store/registration/actions';
import { InnerAccordion } from '../../../../innerAccordion';
import FloorZone from '../floorZone';
import { useCalculateFloorArea } from '../../shapeAndArea/hooks/useCalculateFloorArea';
import { BygningsForm } from '../../../utils/registerEnums';

import { FloorZoneRadioGroup } from './floorZoneRadioGroup';

type FloorSectionProps = {
  etasjeId: string;
};

const FloorSection: FC<FloorSectionProps> = ({ etasjeId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const etasje = useSelector((state) => getEtasje(state, etasjeId), _.isEqual);

  const { area } = useCalculateFloorArea(
    etasje?.bygningsForm ?? BygningsForm.Unknown,
    etasje?.vegger ?? [],
    etasje?.id ?? ''
  );

  if (!etasje) {
    return null;
  }

  const gulvsoner = etasje?.gulvSoner ?? [];
  const multipleFloorZones = gulvsoner.length > 1;
  const isUniformFloor = gulvsoner.length === 1;

  useEffect(() => {
    // If floor is uniform and area can be calculated we programmatically set the value
    if (isUniformFloor && area != 0 && gulvsoner[0].arealIKvm !== area) {
      dispatch(
        updateGulvsone({
          etasjeId,
          gulvsoneId: gulvsoner[0]?.id,
          field: 'arealIKvm',
          value: area
        })
      );
    }
  }, [isUniformFloor, area, gulvsoner, etasjeId, dispatch]);

  const addFloorZone = () => dispatch(addGulvsone({ etasjeId }));
  const removeFloorZone = () => dispatch(removeGulvsone({ etasjeId }));

  const addOrRemoveFloorZone = (val: number) => {
    if (gulvsoner.length > val) {
      return removeFloorZone();
    }

    return addFloorZone();
  };

  return (
    <Fragment>
      <FloorZoneRadioGroup etasje={etasje} multipleZones={multipleFloorZones} />

      {multipleFloorZones && (
        <NumberPicker
          value={gulvsoner?.length || 1}
          marginBottom
          min={2}
          onChange={addOrRemoveFloorZone}
          label={t('register.floors.gulvsoneCount.label')}
        />
      )}

      {multipleFloorZones && (
        <InnerAccordion>
          {gulvsoner?.map((gulvsone, gulvsoneIndex) => (
            <Accordion.Item
              title={
                <Typography bold px={1} component="h4">
                  {t('register.floors.gulvsoneId', {
                    count: gulvsoneIndex + 1
                  })}
                </Typography>
              }
              unmountOnExit={false}
              initExpanded={gulvsoneIndex === 0}
              key={gulvsone.id}
            >
              <Box px={1} my={-2}>
                <FloorZone etasjeId={etasjeId} gulvsoneId={gulvsone.id} gulvsoneIndex={gulvsoneIndex} />
              </Box>
            </Accordion.Item>
          ))}
        </InnerAccordion>
      )}

      {!multipleFloorZones && gulvsoner?.[0]?.id && <FloorZone etasjeId={etasjeId} gulvsoneId={gulvsoner[0].id} />}
    </Fragment>
  );
};

export default FloorSection;
